import React, { useState } from 'react';
import { REPORT_REASONS, ReportReason } from '../types/community';
import { communityService } from '../services/communityService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/PetParksContext';

interface ReportContentModalProps {
  open: boolean;
  onClose: () => void;
  contentType: 'review' | 'photo' | 'message';
  contentId: string;
  contentPreview?: string;
}

export const ReportContentModal: React.FC<ReportContentModalProps> = ({
  open,
  onClose,
  contentType,
  contentId,
  contentPreview,
}) => {
  const { t } = useApp();
  const [reason, setReason] = useState<ReportReason>('spam');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      await communityService.reportContent({
        content_type: contentType,
        content_id: contentId,
        reported_by: 'current-user-id', // TODO: Get from auth
        reason,
        comment: comment || undefined,
      });

      toast.success(t('reportSubmittedSuccess'));
      handleClose();
    } catch (error) {
      toast.error(t('reportSubmitFailed'));
      console.error('Report submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('spam');
    setComment('');
    onClose();
  };

  const contentTypeLabel = {
    review: t('reportTypeReview'),
    photo: t('reportTypePhoto'),
    message: t('reportTypeMessage'),
  }[contentType];

  const reasonLabelMap: Record<ReportReason, string> = {
    spam: t('reportReasonSpam'),
    inappropriate: t('reportReasonInappropriate'),
    harassment: t('reportReasonHarassment'),
    misinformation: t('reportReasonMisinformation'),
    offensive: t('reportReasonOffensive'),
    other: t('reportReasonOther'),
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            {t('reportContentLabel', { type: contentTypeLabel })}
          </DialogTitle>
          <DialogDescription>{t('reportContentDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Content Preview */}
          {contentPreview && (
            <div className="rounded-md border bg-muted p-3">
              <p className="text-sm text-muted-foreground">{t('reporting')}</p>
              <p className="mt-1 line-clamp-2 text-sm">{contentPreview}</p>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-3">
            <Label>{t('reasonForReportingRequired')}</Label>
            <RadioGroup
              value={reason}
              onValueChange={(value) => setReason(value as ReportReason)}
            >
              {REPORT_REASONS.map((r) => (
                <div key={r.value} className="flex items-start space-x-2">
                  <RadioGroupItem
                    value={r.value}
                    id={r.value}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={r.value}
                    className="cursor-pointer font-normal leading-relaxed"
                  >
                    {reasonLabelMap[r.value]}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Additional Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">
              {t('additionalDetails')}{' '}
              {reason === 'other' ? '*' : `(${t('optional')})`}
            </Label>
            <Textarea
              id="comment"
              placeholder={t('reportContextPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Privacy Notice */}
          <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              {t('reportPrivacyNotice')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 cursor-pointer"
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="flex-1 cursor-pointer"
              disabled={isSubmitting || (reason === 'other' && !comment.trim())}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submitReport')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
